import angular from 'angular'

import { zeroIfNaN } from '../utils/bits'
import { clamp, assign } from 'lodash-es'

import StrictObj from '../utils/strict-obj'
import debugFields from '../utils/debug-fields'

import { MOBILE_THRESHOLD } from '../utils/shared-constants'

angular
  .module('ggs.gallery-modal')
  .factory('calcShowcaseSize', ModalShowcaseSizeProvider)

const TEXT_OPTIMAL_MAX_WIDTH = 600 // px
const TEXT_OPTIMAL_MIN_HEIGHT = 150 // px

function ModalShowcaseSizeProvider(debugObj) {

  let isSoftBoundsForced = false
  let isHardBoundsForced = false

  return debugObj('calcShowcaseSize', StrictObj(debugFields(assign(calcShowcaseSize, {
    allowAllBounds,
    forceSoftBounds,
    forceHardBounds
  }), {
    _calcContentSize: calcContentSize,
    _calcOptimal: calcOptimal,
    _calcOptimalXByYMax: calcOptimalXByYMax,
    _calcOptimalYByXMax: calcOptimalYByXMax
  })))

  function allowAllBounds() {
    isSoftBoundsForced = false
    isHardBoundsForced = false
  }

  function forceSoftBounds() {
    isSoftBoundsForced = true
  }

  function forceHardBounds() {
    isHardBoundsForced = true
  }

  function calcShowcaseSize(text, bounds, calcContentWidth, calcContentHeight) {
    if (text) {
      const maxWidth = clamp(TEXT_OPTIMAL_MAX_WIDTH, bounds.soft.minWidth, bounds.soft.maxWidth)
      const minWidth = bounds.soft.minWidth
      const maxHeight = bounds.soft.maxHeight
      const minHeight = clamp(TEXT_OPTIMAL_MIN_HEIGHT, bounds.soft.minHeight, bounds.soft.maxHeight)
      const contentSize = calcOptimalYByXMax('text', maxWidth, maxHeight, minHeight, calcContentHeight)
      contentSize.width = clamp(contentSize.width, minWidth, maxWidth)
      contentSize.height = clamp(contentSize.height, minHeight, maxHeight)
      return contentSize
    } else {
      const contentSize = calcContentSize(bounds, calcContentWidth, calcContentHeight)
      contentSize.width = clamp(contentSize.width, bounds.hard.minWidth, bounds.hard.maxWidth)
      contentSize.height = clamp(contentSize.height, bounds.hard.minHeight, bounds.hard.maxHeight)
      return contentSize
    }
  }

  function calcContentSize(bounds, calcContentWidth, calcContentHeight) {
    const softSize = calcOptimal('soft', bounds.soft, bounds.base, calcContentWidth, calcContentHeight)
    const hardSize = calcOptimal('hard', bounds.hard, bounds.base, calcContentWidth, calcContentHeight)

    if (isHardBoundsForced) {
      return hardSize
    } else if (isSoftBoundsForced) {
      return softSize
    } else if (softSize.fill > 1 && hardSize.fill > 1) {
      return hardSize
    } else if (softSize.fill > 1) {
      return hardSize
    } else if (hardSize.fill > 1) {
      return softSize
    } else if (softSize.fill < 0.6) {
      return hardSize
    } else if (!softSize.fits) {
      return hardSize
    } else if (Math.min(bounds.base.height, bounds.base.width) < MOBILE_THRESHOLD) {
      return hardSize
    } else {
      return softSize
    }
  }

  function calcOptimal(marker, subBounds, base, calcContentWidth, calcContentHeight) {
    const x_by_y_max = calcOptimalXByYMax(marker, subBounds.maxHeight, subBounds.maxWidth, subBounds.minWidth, calcContentWidth)
    const y_by_x_max = calcOptimalYByXMax(marker, subBounds.maxWidth, subBounds.maxHeight, subBounds.minHeight, calcContentHeight)

    if (y_by_x_max.fill > 1 && x_by_y_max.fill > 1) {
      return base.width >= base.height
        ? y_by_x_max
        : x_by_y_max
    } else if (y_by_x_max.fill > 1) {
      return x_by_y_max
    } else if (x_by_y_max.fill > 1) {
      return y_by_x_max
    } else if (Math.abs(x_by_y_max.fill - y_by_x_max.fill) <= 0.1) {
      return base.width >= base.height
        ? y_by_x_max
        : x_by_y_max
    } else {
      return y_by_x_max.fill >= x_by_y_max.fill
        ? y_by_x_max
        : x_by_y_max
    }
  }

  function calcOptimalXByYMax(marker, y_max, x_max, x_min, x_by_y) {
    const x_by_y_max = StrictObj({ marker, method: 'x_by_y_max' })
    x_by_y_max.width = Math.round(x_by_y(y_max))
    x_by_y_max.height = y_max
    x_by_y_max.fits = x_by_y_max.width >= x_min && x_by_y_max.width <= x_max
    x_by_y_max.fill = zeroIfNaN(x_by_y_max.width / x_max)
    return x_by_y_max
  }

  function calcOptimalYByXMax(marker, x_max, y_max, y_min, y_by_x) {
    const y_by_x_max = StrictObj({ marker, method: 'y_by_x_max' })
    y_by_x_max.width = x_max
    y_by_x_max.height = Math.round(y_by_x(x_max))
    y_by_x_max.fits = y_by_x_max.height >= y_min && y_by_x_max.height <= y_max
    y_by_x_max.fill = zeroIfNaN(y_by_x_max.height / y_max)
    return y_by_x_max
  }
}
